/**
 * ServicesStep Component
 * Optional step for selecting additional services (fence protection, lock box, etc.)
 */

import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import type { OrderForm } from '../../../schemas/order.schema';
import type { WidgetService } from '../../../types/order-builder';
import { Plus } from 'lucide-react';
import { ServiceSelector } from '../../../components/widget/ServiceSelector';

interface ServicesStepProps {
  availableServices: WidgetService[];
}

export function ServicesStep({ availableServices }: ServicesStepProps) {
  const { setValue, watch } = useFormContext<OrderForm>();

  // Watch selected services from form
  const watchedServices = watch('selectedServices');
  const selectedServices = useMemo(() => watchedServices || [], [watchedServices]);

  // Extract selected service IDs for ServiceSelector
  const selectedServiceIds = useMemo(() => {
    if (!selectedServices) return [];
    return selectedServices.map(s => s.serviceId);
  }, [selectedServices]);

  // Handle service toggle
  const handleServiceToggle = (serviceId: string) => {
    const isSelected = selectedServiceIds.includes(serviceId);

    if (isSelected) {
      // Remove service
      const updated = selectedServices.filter(s => s.serviceId !== serviceId);
      setValue('selectedServices', updated, { shouldValidate: true, shouldDirty: true });
    } else {
      // Add service with default quantity of 1
      const updated = [
        ...selectedServices,
        { serviceId, quantity: 1 }
      ];
      setValue('selectedServices', updated, { shouldValidate: true, shouldDirty: true });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50 rounded-lg">
            <Plus className="h-6 w-6 text-primary-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">
            Additional Services
          </h2>
        </div>
        <p className="text-lg text-gray-600 leading-relaxed">
          Enhance your order with optional add-on services (optional).
        </p>
      </div>

      {/* Services Selection */}
      {availableServices.length > 0 ? (
        <ServiceSelector
          availableServices={availableServices}
          selectedServiceIds={selectedServiceIds}
          onServiceToggle={handleServiceToggle}
        />
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <p className="text-gray-600">
            No additional services are available for this provider.
          </p>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex">
          <svg
            className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              <span className="font-medium">Optional:</span> You can skip this step if you don&apos;t need any additional services.
              Services can enhance your delivery experience with added convenience and protection.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
